import { Node } from "reactflow";

import { DefaultNodeData, ZapNodeData } from "@zapier/canvas-state";
import { ASSET_APP_IDS } from "@zapier/canvas-constants";

type KeysToClearFromDefaultNode = keyof Pick<
  DefaultNodeData,
  | "label"
  | "app"
  | "services"
  | "tableId"
  | "projectId"
  | "pageId"
  | "chatbotId"
  | "color"
>;
type KeysToClearFromZapNode = keyof ZapNodeData;
type KeysToClearWhenNoAppSelected = keyof Pick<
  DefaultNodeData,
  "tableId" | "projectId" | "pageId" | "chatbotId" | "app"
>;

type DataKeysByNodeType = {
  default: KeysToClearFromDefaultNode[];
  zap: KeysToClearFromZapNode[];
};

const dataKeysByNodeType: DataKeysByNodeType = {
  default: [
    "color",
    "app",
    "services",
    "tableId",
    "projectId",
    "pageId",
    "chatbotId",
  ],
  zap: ["zapId", "isExpanded"],
};

type KeysToClearByAssetType = {
  tables: ("projectId" | "pageId" | "chatbotId")[];
  interfaces: ("tableId" | "chatbotId")[];
  chatbots: ("projectId" | "pageId" | "tableId")[];
};

const keysToClearByAssetType: KeysToClearByAssetType = {
  tables: ["projectId", "pageId", "chatbotId"],
  interfaces: ["tableId", "chatbotId"],
  chatbots: ["projectId", "pageId", "tableId"],
};

const keysToClearWhenNoApp: KeysToClearWhenNoAppSelected[] = [
  "app",
  "tableId",
  "projectId",
  "pageId",
  "chatbotId",
];

function clearKeys(data: object, keysToClear: string[]) {
  keysToClear.forEach((key) => {
    delete (data as { [key: string]: unknown })[key];
  });
}

/**
 * Clears out specific data fields from the given node based on its type.
 *
 * For nodes of type 'zap', fields specific to 'default' nodes are removed.
 * For nodes of type 'default', fields specific to 'zap' nodes are removed.
 */
function clearOutDataFromNode(node: Node) {
  if (!node.data) {
    return node;
  }

  // Create a new data object to avoid mutating the original
  const newData = { ...node.data };

  let keysToClear: string[] = [];

  if (node.type === "zap") {
    keysToClear = dataKeysByNodeType.default;
  } else if (node.type === "default") {
    keysToClear = dataKeysByNodeType.zap;
  }

  clearKeys(newData, keysToClear);

  if (!newData.services || newData.services.length === 0) {
    clearKeys(newData, keysToClearWhenNoApp);
  } else {
    const appId: string =
      newData.services.length > 0
        ? newData.services[0].currentImplementation.id
        : "";

    const isTableApp = appId.includes(ASSET_APP_IDS.TABLES);
    const isInterfacesApp = appId.includes(ASSET_APP_IDS.INTERFACES);
    const isChatbotsApp = appId.includes(ASSET_APP_IDS.CHATBOTS);

    if (isTableApp) {
      clearKeys(newData, keysToClearByAssetType.tables);
    } else if (isInterfacesApp) {
      clearKeys(newData, keysToClearByAssetType.interfaces);
    } else if (isChatbotsApp) {
      clearKeys(newData, keysToClearByAssetType.chatbots);
    }
  }

  return { ...node, data: newData };
}

export default clearOutDataFromNode;
